import { Alert, AlertTitle, Box, Button, Card, CircularProgress, Container, MenuItem, Stack, Table, TableBody, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { memo, useState } from "react";
import { enqueueSnackbar } from "notistack";
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import Iconify from "src/components/iconify/iconify";
import { getQuarterStartDate, getQuarterEndDate, generateYearRange, getCurrentQuarter, getCurrentYear, today, addDaysToDate } from "src/utils/date-utils";
import PageHeader from "src/components/page-header";
import TruxSimpleDateField from "src/components/inputs/trux-simple-date-field";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import { TruxTableCell } from "src/components/trux-table-cell";
import NoneFoundReport from "src/components/none-found-report";

type PerVehicleLine = {
  truckNumber: string;
  miles: string;
};

type PerVehicleResponse = {
  lines: PerVehicleLine[];
  totalMiles: string;
};

const formatMiles = (miles: string) =>
  parseFloat(miles).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const PerVehicleTable = memo(({ lines }: { lines: PerVehicleLine[] }) => {
  const { sortedList, sortData } = useSort(lines);
  if (!sortedList) return null;

  return (
    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <SortableHeader sortData={sortData} sortKey="truckNumber">Truck Number</SortableHeader>
              <SortableHeader sortData={sortData} sortKey="miles" align="right">Total Miles</SortableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedList.map((line, i) => (
              <TableRow key={i}>
                <TruxTableCell>{line.truckNumber}</TruxTableCell>
                <TruxTableCell align="right">{formatMiles(line.miles)} mi</TruxTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
});

export default function Page() {
  const settings = useSettingsContext();

  const currentYear = getCurrentYear();
  const currentQuarter = getCurrentQuarter();
  const yearRange = generateYearRange(5, 5);

  const [year, setYear] = useState(currentYear);
  const [quarter, setQuarter] = useState(currentQuarter);
  const [byDate, setByDate] = useState(false);
  const [fromDateParam, setFromDateParam] = useState(addDaysToDate(today(), -30));
  const [toDateParam, setToDateParam] = useState(today());

  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [ran, setRan] = useState(false);
  const [data, setData] = useState<PerVehicleResponse | null>(null);

  const fromDate = byDate ? fromDateParam : getQuarterStartDate(year, quarter);
  const toDate = byDate ? toDateParam : getQuarterEndDate(year, quarter);

  const handleRun = () => {
    setLoading(true);
    setRan(true);
    axios.get(formatEndpoint(endpoints.eldIftaReportPerVehicle, { fromDate, toDate }))
      .then((response) => {
        setLoading(false);
        setData(response.data);
      })
      .catch(() => {
        setLoading(false);
        enqueueSnackbar("Unexpected exception has occurred. Please contact customer support.", { variant: "error" });
      });
  };

  const handleDownloadCsv = () => {
    if (!ran) {
      enqueueSnackbar("Please run the report first.", { variant: "error" });
      return;
    }
    setDownloading(true);
    axios({ url: formatEndpoint(endpoints.eldIftaReportPerVehicleCsv, { fromDate, toDate }), method: 'GET', responseType: 'blob' })
      .then((response) => {
        setDownloading(false);
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `IFTA Mileage by Vehicle ${fromDate} to ${toDate}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {
        setDownloading(false);
        enqueueSnackbar("Unexpected exception has occurred. Please contact customer support.", { variant: "error" });
      });
  };

  const handleDownloadPdf = () => {
    if (!ran) {
      enqueueSnackbar("Please run the report first.", { variant: "error" });
      return;
    }
    setDownloading(true);
    axios({ url: formatEndpoint(endpoints.eldIftaReportPerVehiclePdf, { fromDate, toDate }), method: 'GET', responseType: 'blob' })
      .then((response) => {
        setDownloading(false);
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const pdfWindow = window.open();
        if (pdfWindow) {
          pdfWindow.location.href = url;
        }
      })
      .catch((e) => {
        console.error(e);
        setDownloading(false);
        enqueueSnackbar("Unexpected exception has occurred. Please contact customer support.", { variant: "error" });
      });
  };

  const hasLines = data !== null && data.lines.length > 0;
  const isEmpty = ran && data !== null && data.lines.length === 0;

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <PageHeader title="IFTA Mileage by Vehicle">
        <Button
          variant="outlined"
          startIcon={downloading ? <CircularProgress size={20} /> : <Iconify icon="vscode-icons:file-type-excel2" />}
          onClick={handleDownloadCsv}
          disabled={downloading || !ran}
        >
          {downloading ? 'Downloading...' : 'Download CSV'}
        </Button>
        <Button
          variant="outlined"
          startIcon={downloading ? <CircularProgress size={20} /> : <Iconify icon="vscode-icons:file-type-pdf2" />}
          onClick={handleDownloadPdf}
          disabled={downloading || !ran}
        >
          {downloading ? 'Downloading...' : 'Download PDF'}
        </Button>
      </PageHeader>

      <Alert
        icon={<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}><Iconify icon="mdi:robot-outline" /></Box>}
        severity="success"
        sx={{ mb: 2 }}
      >
        <AlertTitle>AI-Powered Mileage Recovery</AlertTitle>
        When the ELD is temporarily disconnected, our system detects missing miles using odometer readings and intelligently distributes them across states based on your recorded travel patterns. This ensures your IFTA report captures all miles driven.
      </Alert>

      <Card sx={{ mb: 2 }}>
        <Stack gap={1} direction="row" flexWrap="wrap" alignItems="center" sx={{ p: 2 }}>
          <TextField
            select
            label="By"
            value={byDate ? "date" : "quarter"}
            onChange={(e) => setByDate(e.target.value === "date")}
            size="small"
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="quarter">Quarter</MenuItem>
            <MenuItem value="date">Date</MenuItem>
          </TextField>

          {byDate ? (
            <>
              <TruxSimpleDateField label="From Date" value={fromDateParam} onChange={d => setFromDateParam(d!)} />
              <TruxSimpleDateField label="To Date" value={toDateParam} onChange={d => setToDateParam(d!)} />
            </>
          ) : (
            <>
              <TextField
                select
                label="Year"
                value={year}
                onChange={(e) => setYear(e.target.value as any)}
                size="small"
                sx={{ minWidth: 100 }}
              >
                {yearRange.map(yearOption => (
                  <MenuItem key={yearOption} value={yearOption}>{yearOption}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Quarter"
                value={quarter}
                onChange={(e) => setQuarter(Number(e.target.value))}
                size="small"
                sx={{ minWidth: 160 }}
              >
                <MenuItem value={1}>Q1 (Jan - Mar)</MenuItem>
                <MenuItem value={2}>Q2 (Apr - Jun)</MenuItem>
                <MenuItem value={3}>Q3 (Jul - Sep)</MenuItem>
                <MenuItem value={4}>Q4 (Oct - Dec)</MenuItem>
              </TextField>
            </>
          )}

          <Button variant="contained" onClick={handleRun} disabled={loading}>
            {loading ? 'Running...' : 'Run Report'}
          </Button>
        </Stack>
      </Card>

      {!loading && hasLines && (
        <Box sx={{ px: 2, py: 1.5, mb: 2, borderRadius: 1, bgcolor: 'background.neutral' }}>
          <Typography variant="subtitle2">
            Total Distance: &nbsp;&nbsp;<Typography component="span" variant="subtitle1" fontWeight={700}>{formatMiles(data!.totalMiles)} mi</Typography>
          </Typography>
        </Box>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {!loading && hasLines && <PerVehicleTable lines={data!.lines} />}
      {!loading && isEmpty && <NoneFoundReport />}
    </Container>
  );
}
